import React, { useState, useEffect } from 'react';
import { Photo, PhotoCategory } from '../types/Photo';
import './PhotoGallery.css';
import mediaService from '../services/mediaService';

// Données statiques de secours en cas d'échec de l'API
const FALLBACK_PHOTOS: Record<PhotoCategory, Photo[]> = {
  logo: [
    { 
      _id: '507f1f77bcf86cd799439001' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721001/logo/logo-exemple-1.jpg',
      description: 'Logo exemple 1',
      category: 'logo',
      created_at: new Date()
    },
    { 
      _id: '507f1f77bcf86cd799439002' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721002/logo/logo-exemple-2.jpg',
      description: 'Logo exemple 2',
      category: 'logo',
      created_at: new Date()
    }
  ],
  portrait: [
    { 
      _id: '507f1f77bcf86cd799439011' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721011/portrait/portrait-exemple-1.jpg',
      description: 'Portrait exemple 1',
      category: 'portrait',
      created_at: new Date()
    },
    { 
      _id: '507f1f77bcf86cd799439012' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721012/portrait/portrait-exemple-2.jpg',
      description: 'Portrait exemple 2',
      category: 'portrait',
      created_at: new Date()
    }
  ],
  Voitures: [
    { 
      _id: '507f1f77bcf86cd799439031' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721031/voitures/voiture-exemple-1.jpg',
      description: 'Voiture exemple 1',
      category: 'Voitures',
      created_at: new Date()
    },
    { 
      _id: '507f1f77bcf86cd799439032' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721032/voitures/voiture-exemple-2.jpg',
      description: 'Voiture exemple 2',
      category: 'Voitures',
      created_at: new Date()
    }
  ],
  Paysages: [
    { 
      _id: '507f1f77bcf86cd799439021' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721021/paysages/paysage-exemple-1.jpg',
      description: 'Paysage de montagne',
      category: 'Paysages',
      created_at: new Date()
    },
    { 
      _id: '507f1f77bcf86cd799439022' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721022/paysages/paysage-exemple-2.jpg',
      description: 'Paysage de mer',
      category: 'Paysages',
      created_at: new Date()
    }
  ],
  Tournages: [
    { 
      _id: '507f1f77bcf86cd799439041' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721041/tournages/tournage-exemple-1.jpg',
      description: 'Tournage exemple 1',
      category: 'Tournages',
      created_at: new Date()
    },
    { 
      _id: '507f1f77bcf86cd799439042' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721042/tournages/tournage-exemple-2.jpg',
      description: 'Tournage exemple 2',
      category: 'Tournages',
      created_at: new Date()
    }
  ],
  Evenement: [
    { 
      _id: '507f1f77bcf86cd799439051' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721051/evenement/evenement-exemple-1.jpg',
      description: 'Événement exemple 1',
      category: 'Evenement',
      created_at: new Date()
    },
    { 
      _id: '507f1f77bcf86cd799439052' as any, 
      url: 'https://res.cloudinary.com/dzfejss9r/image/upload/v1694721052/evenement/evenement-exemple-2.jpg',
      description: 'Événement exemple 2',
      category: 'Evenement',
      created_at: new Date()
    }
  ]
};

interface InstagramGalleryProps {
  category: PhotoCategory;
}

const InstagramGallery: React.FC<InstagramGalleryProps> = ({ category }) => {
  const [photos, setPhotos] = useState<Photo[]>([]);
  const [loading, setLoading] = useState<boolean>(true);
  const [error, setError] = useState<string | null>(null);
  const [useFallback, setUseFallback] = useState<boolean>(false);

  useEffect(() => {
    const fetchImages = async () => {
      setLoading(true);
      setError(null);

      try {
        // mediaService fusionne déjà images et vidéos pour la catégorie demandée
        const fetchedPhotos = await mediaService.getByCategory(category);

        if (fetchedPhotos.length > 0) {
          setPhotos(fetchedPhotos);
        } else {
          console.log(`Aucun média trouvé pour la catégorie ${category}, utilisation des données de secours`);
          setUseFallback(true);
          setPhotos(FALLBACK_PHOTOS[category] || []);
        }
      } catch (err) {
        console.error(`Erreur lors de la récupération des médias pour ${category}:`, err);
        setError(`Erreur lors du chargement des médias: ${err instanceof Error ? err.message : 'Erreur inconnue'}`);
        setUseFallback(true);
        setPhotos(FALLBACK_PHOTOS[category] || []);
      } finally {
        setLoading(false);
      }
    };

    fetchImages();
  }, [category]);

  if (loading) {
    return (
      <div className="flex justify-center items-center py-10">
        <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-gray-900"></div>
      </div>
    );
  }

  if (error && photos.length === 0) {
    return (
      <div className="text-center py-10">
        <p className="text-red-500">{error}</p>
        {useFallback && (
          <p className="mt-2 text-gray-600">Affichage des exemples en attendant la connexion au serveur.</p>
        )}
      </div>
    );
  }

  if (photos.length === 0) {
    return (
      <div className="text-center py-10">
        <p>Aucune photo disponible dans cette catégorie.</p>
      </div>
    );
  }

  // Fonction utilitaire pour générer une clé unique sécurisée
  const generateKey = (photo: Photo, index: number): string => {
    if (photo._id) {
      return photo._id.toString();
    }
    return `photo-${category}-${index}-${Math.random().toString(36).substring(2, 9)}`;
  };

  return (
    <div className="instagram-gallery bg-[#0B0B0F]" id="photo-gallery">
      {useFallback && (
        <div className="text-center py-2 mb-4 bg-yellow-100 text-yellow-800 rounded">
          <p>Mode hors ligne: affichage des exemples.</p>
        </div>
      )}
      {photos.map((photo, index) => (
        <div
        key={generateKey(photo, index)}
        className="instagram-item flex justify-center items-center bg-[#0B0B0F]"
      >
        <img
          src={photo.url}
          alt={photo.description || ""}
          className="w-auto h-auto max-w-full "
          loading="lazy"
        />
        <div className="instagram-overlay bg-[#0B0B0F]"></div>
      </div>
      ))}
    </div>
  );
};

export default InstagramGallery;
